import { useFormContext } from "react-hook-form";
import React, { useCallback, useEffect, useState } from "react";
import _ from "lodash";
import { DesignSuggestion, DesignSuggestionValue, USER_FILLABLE } from "../AllDesignSuggestions";
import { addParameterToJSClass } from "../JSClasses";
import { Box, Button, Card, CardActions, Divider, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import TranslationWrapper from "../../../../../../components/TranslationWrapper/TranslationWrapper";
import { ParameterBadge } from "../JamezzStylesComponent";
import { JamezzStylesCSSInput } from "../JamezzStylesCSSInput";
import { SxProperties } from "../../CustomComponents";
import { JSClassParameter } from "../../../../../../components/LiveInterfaceCustomComponents/LiveInterface";

/**
 * `useRememberFormContextValues` is a custom React hook that helps with remembering and restoring
 * form values using React Hook Form's useFormContext.
 *
 * It is particularly useful when you want to experiment with a set of options and preview the results.
 * You can show the preview, and either accept it or cancel it at any time.
 *
 * @param {ReturnType<typeof useFormContext>} context - The form context returned by `useFormContext`.
 * @param {string[]} keys - The list of keys which corresponds to the fields you want to remember.
 *
 * @returns {ReturnType<typeof useRememberFormContextValues>} An object with two callback functions: `remember` and `restore`.
 *
 * - `remember` - Stores currently provided keys values from form context into state.
 * - `restore` - Sets previously remembered values back into form context.
 *
 * @example
 * import { useFormContext } from 'react-hook-form';
 * ...
 * const context = useFormContext();
 * const { remember, restore } = useRememberFormContextValues(context, ['firstName', 'lastName']);
 *
 * // Remember the current values
 * remember();
 *
 * // Restore the remembered values
 * restore();
 */
function useRememberFormContextValues(context: ReturnType<typeof useFormContext>, keys: string[]) {
  const [rememberedValues, setRememberedValues] = useState<[string, any][]>([]);
  const remember = useCallback(() => {
    setRememberedValues(_.zip(keys, context.getValues(keys)) as [string, any][]);
  }, []);

  const restore = useCallback(() => {
    context.reset();
    rememberedValues.forEach(([key, value]) => {
      context.setValue(key, value);
    });
  }, [rememberedValues]);

  return {
    remember,
    restore,
  };
}

function assertIsDetailedValue(value: DesignSuggestionValue): asserts value is {
  value: typeof USER_FILLABLE;
  description: string;
} {
  if (typeof value !== "object") {
    throw new Error("Not a detailed value");
  }
}

function assertIsPreconfiguredValue(value: DesignSuggestionValue): asserts value is string {
  if (typeof value !== "string") {
    throw new Error("Not a preconfigured value");
  }
}

export function DesignSuggestionForm({
  suggestionName,
  designSuggestions,
  onFinish,
  availableParameters,
}: {
  suggestionName: string;
  designSuggestions: DesignSuggestion[];
  onFinish: (accepted: boolean) => void;
  availableParameters: JSClassParameter[];
}) {
  const designSuggestion = designSuggestions.find(
    (designSuggestion) => designSuggestion.suggestionName === suggestionName
  );
  const formCtx = useFormContext();
  const [formRef, setFormRef] = useState<HTMLFormElement | null>(null);
  const [formValid, setFormValid] = useState<boolean>(false);
  const [chosenParameter, setChosenParameter] = useState<"all" | JSClassParameter["parameterName"] | null>(null);

  if (!designSuggestion) return null;

  const chosenParamToJSClassString = (
    className: `.JS-${string}` | `${string}`,
    chosenParameter: JSClassParameter["parameterName"] | "all" | null
  ) => {
    const param = availableParameters.find((param) => param.parameterName === chosenParameter);
    return chosenParameter === null || chosenParameter === "all" || param === undefined
      ? className
      : addParameterToJSClass(className, chosenParameter, param.value);
  };

  const setStylingFromDesignSuggestions = () => {
    designSuggestion.styles.forEach(({ className, values }) =>
      Object.entries(values).forEach(([cssPropName, valueConfig]) => {
        const JSClassParam = chosenParamToJSClassString(className, chosenParameter);
        let value = null;
        try {
          assertIsDetailedValue(valueConfig);
          if ("defaultValue" in valueConfig) {
            value = valueConfig.defaultValue;
          }
        } catch (e) {
          value = valueConfig as "string" | typeof USER_FILLABLE;
        }
        if (value !== USER_FILLABLE) {
          formCtx.setValue(`custom_component_styling${JSClassParam}.${cssPropName}`, value, { shouldDirty: true });
        }
      })
    );
  };

  const allKeys = _.flattenDeep(
    designSuggestion.styles.map(({ className, values }) => {
      const JSClassParam = chosenParamToJSClassString(className, chosenParameter);
      return Object.keys(values).map((key) => `custom_component_styling${JSClassParam}.${key}`);
    })
  );

  const { restore, remember } = useRememberFormContextValues(formCtx, allKeys);

  const propertiesTheUserShouldEdit = designSuggestion.styles
    .map((stylesForJSClassname) => ({
      ...stylesForJSClassname,
      values: _.pickBy(
        stylesForJSClassname.values,
        (value) => value === USER_FILLABLE || value?.value === USER_FILLABLE
      ),
    }))
    .filter((filteredStylesPerJSClassname) => Object.keys(filteredStylesPerJSClassname).length > 0);
  const hasEditableProperties =
    propertiesTheUserShouldEdit.reduce((total, current) => {
      const add = Object.keys(current.values).length > 0 ? 1 : 0;
      return total + add;
    }, 0) > 0;

  const steps: ("apply-to" | "properties" | "confirmation")[] = [];
  if (availableParameters.length > 0) {
    steps.push("apply-to");
  }

  if (hasEditableProperties) {
    steps.push("properties");
  }
  steps.push("confirmation");
  const [currentStep, setCurrentStep] = useState<number>(0);

  useEffect(() => {
    // before setting the suggested properties, remember the original values,
    // so we can restore to them later
    remember();

    // after remembering, we will set all suggested properties with a value in one go.
    setStylingFromDesignSuggestions();
  }, []);

  useEffect(() => {
    if (chosenParameter !== null) {
      restore();
      setStylingFromDesignSuggestions();
    }
  }, [chosenParameter]);

  let nextButtonDisabled = false;
  if (steps[currentStep] === "apply-to") {
    nextButtonDisabled = chosenParameter === null;
  } else if (steps[currentStep] === "properties") {
    nextButtonDisabled = !formValid;
  }

  return (
    <Card sx={{ color: "#3c4858", ml: 2 }} variant={"outlined"}>
      <Box sx={{ backgroundColor: "#fafafa", p: 2 }}>
        <h4 style={{ margin: 0, marginBottom: "8px" }}>{designSuggestion.suggestionName}</h4>
        <p>{designSuggestion.description}</p>
      </Box>
      <Box sx={{ p: 2 }}>
        {steps[currentStep] === "apply-to" ? (
          <>
            <h5 style={{ marginBottom: "16px" }}>
              <TranslationWrapper
                tDefault={"Choose what components this design should apply to"}
                tKey={"live-interface.choose-component-parameters"}
              />
            </h5>
            <ToggleButtonGroup
              exclusive
              onChange={(e, newValue) => {
                if (newValue) setChosenParameter(newValue);
              }}
              sx={{
                mb: 1,
                ml: "4px",
              }}
              value={chosenParameter}
            >
              <ToggleButton key={"all"} value={"all"}>
                <TranslationWrapper tDefault={"All"} tKey={"live-interface.all"} />
              </ToggleButton>
              {availableParameters.map((param) => (
                <ToggleButton key={param.parameterName} value={param.parameterName}>
                  <TranslationWrapper tDefault={"Only"} tKey={"liveInterface.component-button-text"} />
                  &nbsp;
                  <ParameterBadge parameterName={param.parameterName} value={param.value} />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </>
        ) : null}
        {steps[currentStep] === "properties" ? (
          <>
            <h5 style={{ margin: 0, marginBottom: "8px" }}>
              <TranslationWrapper
                tDefault={"Change the styles below to your liking"}
                tKey={"live-interface.adjust-styles"}
              />
            </h5>
            <form
              onChange={() => {
                if (formRef) {
                  setFormValid(formRef.checkValidity());
                }
              }}
              onSubmit={(e) => e.preventDefault()}
              ref={(newFormRef) => {
                if (newFormRef) {
                  setFormValid(newFormRef.checkValidity());
                  setFormRef(newFormRef);
                }
              }}
            >
              {propertiesTheUserShouldEdit.map((styles) => {
                return Object.entries(styles.values).map(([key, valueConfig]) => {
                  let value, description;
                  let shouldOverrideValue = false;
                  let required = false;
                  try {
                    assertIsDetailedValue(valueConfig);
                    description = valueConfig.description;
                    required = Boolean(valueConfig.required);
                    if (valueConfig.defaultValue) {
                      value = valueConfig.defaultValue;
                      shouldOverrideValue = true;
                    }
                  } catch (e) {
                    try {
                      assertIsPreconfiguredValue(valueConfig);
                      value = valueConfig;
                      shouldOverrideValue = true;
                    } catch (e) {}
                  }
                  const jsClassName = chosenParamToJSClassString(styles.className, chosenParameter);

                  return (
                    <Box
                      key={key}
                      style={{
                        padding: 5,
                        paddingBottom: 15,
                      }}
                    >
                      <Typography
                        sx={{
                          padding: "4px",
                          color: "rgba(0, 0, 0, 0.54)",
                          fontWeight: 400,
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          fontSize: "1rem",
                        }}
                      >
                        {description ? description : key}
                      </Typography>
                      <JamezzStylesCSSInput
                        JSClassName={jsClassName}
                        fonts={[]}
                        key={chosenParameter}
                        required={required}
                        valueKey={key as SxProperties}
                        {...(shouldOverrideValue
                          ? {
                              overrideValue: value,
                            }
                          : {})}
                      />
                    </Box>
                  );
                });
              })}
            </form>
          </>
        ) : null}
        {steps[currentStep] === "confirmation" ? (
          <CardActions sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Button
              color={"success"}
              onClick={() => {
                onFinish(true);
              }}
              size={"small"}
            >
              <TranslationWrapper tDefault={"Apply the design"} tKey={"live-interface.apply-suggested-design"} />
            </Button>
            <Button
              onClick={() => {
                restore();
                onFinish(false);
              }}
              size={"small"}
            >
              <TranslationWrapper
                tDefault={"Do not apply the design"}
                tKey={"live-interface.dont-apply-suggested-design"}
              />
            </Button>
          </CardActions>
        ) : null}
        {steps[currentStep] !== "confirmation" ? (
          <>
            <Divider sx={{ my: 4 }} />
            <CardActions sx={{ display: "grid" }}>
              <Button
                disabled={nextButtonDisabled}
                onClick={() => {
                  setCurrentStep((s) => s + 1);
                }}
              >
                <TranslationWrapper tDefault={"next"} tKey={"live-interface.next"} />
              </Button>
            </CardActions>
          </>
        ) : null}
      </Box>
    </Card>
  );
}
